import React, { useEffect, useState } from "react";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, EmptyState } from "../components/Common";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Users, LineChart as LineChartIcon, Stethoscope, FlaskConical } from "lucide-react";

const COLORS = ["#1976D2","#2E7D32","#F59E0B","#D32F2F","#64748B","#0EA5E9","#8B5CF6"];

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/hub").then(r=>setData(r.data)).catch(()=>{}); }, []);
  if (!data) return <div className="p-8 text-slate-500">Loading reports…</div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reports-hub">
      <PageHeader title="Reports & Analytics" subtitle={`Cross-department view · period ${data.period}`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="New patients (MTD)" value={data.patients_month} icon={Users} tone="blue" testId="kpi-patients-month" />
        <KpiCard label="Avg lab TAT" value={`${data.lab_tat.avg_hours} h`} icon={FlaskConical} tone="green" hint={`${data.lab_tat.count} verified`} />
        <KpiCard label="Active doctors" value={data.doctor_productivity.length} icon={Stethoscope} tone="slate" />
        <KpiCard label="Top diagnosis" value={data.diagnosis_top[0]?.diagnosis || "—"} icon={LineChartIcon} tone="amber" hint={data.diagnosis_top[0]? `${data.diagnosis_top[0].count} cases` : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Revenue by category (MTD, paid)">
          <div className="p-4 h-64">
            {data.revenue_by_category.length===0 ? <EmptyState title="No paid invoices yet this month" /> : (
              <ResponsiveContainer><BarChart data={data.revenue_by_category}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="category" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v)=>UGX(v)} />
                <Bar dataKey="total" radius={[6,6,0,0]}>{data.revenue_by_category.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}</Bar>
              </BarChart></ResponsiveContainer>
            )}
          </div>
        </SectionCard>
        <SectionCard title="Top diagnoses (from signed SOAP notes)">
          {data.diagnosis_top.length===0 ? <EmptyState title="No diagnoses recorded" /> : (
            <ul className="divide-y divide-slate-100">
              {data.diagnosis_top.map((d,i)=>(
                <li key={i} className="p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-800">{d.diagnosis}</span>
                  <span className="tabular text-sm font-semibold">{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Doctor productivity (last 30 days)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2.5">Doctor</th><th className="text-right px-4 py-2.5">Notes</th><th className="text-right px-4 py-2.5">Signed</th><th className="text-right px-4 py-2.5">Signing rate</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.doctor_productivity.map((d,i)=>(
                <tr key={i}><td className="px-4 py-2.5">{d.author}</td>
                  <td className="px-4 py-2.5 text-right tabular">{d.notes}</td>
                  <td className="px-4 py-2.5 text-right tabular">{d.signed}</td>
                  <td className="px-4 py-2.5 text-right tabular">{d.notes ? Math.round((d.signed/d.notes)*100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
